import i18n from "i18next";
import { useFieldArray } from "react-hook-form";
import { faPlus, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { requiredNonNegativeFloatRule, requireRule } from "src/components/common/forms/validation.ts";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { Typography } from "@mui/material";
import { useAsyncFetch } from "src/utils/async/asyncFetch.ts";
import { logError } from "src/errorHandling/errorLogging.ts";
import { useEffect, useRef } from "react";
import { PartView } from "src/api/generated/erp/db/types/tables/partView.ts";
import { PartCostOverrideOptionsProvider_PropertyValueOption } from "src/api/generated/erp/sales/partCostOverride/partCostOverrideOptionsProvider.ts";
import { getPartLongName } from "src/components/views/erp/erpUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import {
	PartCostOverrideOwnerApi,
	PartCostOverrideRow,
} from "src/components/views/erp/sales/partCostOverride/partCostOverrideOwnerApi.ts";
import { getPartCostOverrideConditionOptions } from "src/components/views/erp/sales/partCostOverride/partCostOverrideConditionOptions.ts";

export interface PartCostOverrideFormProps extends FormCommonProps<void> {
	api: PartCostOverrideOwnerApi;
	existingPartCostOverride: PartCostOverrideRow | undefined;
}

interface FormValues {
	partId: number;
	conditions: Array<{ propertyName: string; propertyValue: string }>;
	unitCost: number;
}

export function PartCostOverrideForm({
	api,
	existingPartCostOverride,
	onCompleted,
	onFormEdited,
}: PartCostOverrideFormProps) {
	return (
		<AsyncForm<PartView[], FormValues, void>
			fetch={api.getPartOptions}
			getDefaultValues={() =>
				existingPartCostOverride == null ?
					{ conditions: [] }
				:	{
						partId: existingPartCostOverride.partId,
						conditions: existingPartCostOverride.propertyConditions.conditions,
						unitCost: existingPartCostOverride.unitCost,
					}
			}
			submit={submit}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.partCostOverrides.form"),
				title: i18n.t("part_cost_override"),
				pathSegments: [i18n.t("form")],
				data: { partCostOverrideId: existingPartCostOverride?.partCostOverrideId ?? null },
			}}
			render={(contentParams) => (
				<FormContent {...contentParams} api={api} existingPartCostOverride={existingPartCostOverride} />
			)}
		/>
	);

	async function submit(values: FormValues) {
		const partCostOverride = {
			partId: values.partId,
			propertyConditions: { conditions: values.conditions },
			unitCost: values.unitCost,
		};
		if (existingPartCostOverride == null) await api.createPartCostOverride(partCostOverride);
		else await api.updatePartCostOverride(existingPartCostOverride.partCostOverrideId, partCostOverride);
	}
}

interface FormContentProps extends AsyncFormContentParams<PartView[], FormValues> {
	api: PartCostOverrideOwnerApi;
	existingPartCostOverride: PartCostOverrideRow | undefined;
}

function FormContent({ control, watch, setValue, data: partOptions, api, existingPartCostOverride }: FormContentProps) {
	const partId = watch("partId");
	const selectedPart = partOptions.find((part) => part.partId === partId);
	const conditionFieldArray = useFieldArray({ control, name: "conditions" });
	const [valueOptions, reloadValueOptions] = useAsyncFetch<
		PartCostOverrideOptionsProvider_PropertyValueOption[],
		number
	>(
		(selectedPartId) =>
			selectedPartId == null ? Promise.resolve([]) : api.getPropertyValueOptions(selectedPartId),
		{ fetchOnMount: false, defaultValue: [] },
	);
	const previousPartId = useRef(partId);
	useEffect(() => {
		reloadValueOptions(partId).catch(logError);
		// Conditions refer to the properties of the previously selected part.
		if (previousPartId.current !== partId) setValue("conditions", []);
		previousPartId.current = partId;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [partId]);
	const conditionOptions = getPartCostOverrideConditionOptions(
		valueOptions.data ?? [],
		existingPartCostOverride?.partId === partId ?
			(existingPartCostOverride?.propertyConditions.conditions ?? [])
		:	[],
	);

	return (
		<>
			<FormSelectField
				control={control}
				name={"partId"}
				label={i18n.t("part")}
				rules={requireRule()}
				options={partOptions}
				getOptionKey={(part) => part.partId}
				getOptionLabel={getPartLongName}
				autoFocus
			/>
			<FormNumberField
				control={control}
				name={"unitCost"}
				label={i18n.t("unit_cost")}
				rules={requiredNonNegativeFloatRule()}
			/>
			{selectedPart?.isConfigurable === true && (
				<VerticalBox sx={{ gap: 1 }}>
					<Typography variant={"subtitle2"}>{i18n.t("part_cost_override_property_conditions")}</Typography>
					{conditionFieldArray.fields.map((field, index) => {
						const propertyName = watch(`conditions.${index}.propertyName`);
						return (
							<HorizontalBox key={field.id} sx={{ gap: 1, alignItems: "center" }}>
								<FormSelectField
									control={control}
									name={`conditions.${index}.propertyName`}
									label={i18n.t("property")}
									rules={requireRule()}
									options={conditionOptions.propertyNames}
									getOptionKey={(name) => name}
									getOptionLabel={(name) => name}
									// A value of the previous property would otherwise stay hidden in the form.
									onChange={() => setValue(`conditions.${index}.propertyValue`, "")}
									sx={{ flex: 1 }}
								/>
								<FormSelectField
									control={control}
									name={`conditions.${index}.propertyValue`}
									label={i18n.t("value")}
									rules={requireRule()}
									options={conditionOptions.valuesByPropertyName[propertyName] ?? []}
									getOptionKey={(option) => option.propertyValue}
									getOptionLabel={(option) => option.label}
									sx={{ flex: 1 }}
								/>
								<AavoButton icon={faTimes} onClick={() => conditionFieldArray.remove(index)} />
							</HorizontalBox>
						);
					})}
					<AavoButton
						icon={faPlus}
						label={i18n.t("part_cost_override_add_condition")}
						variant={"outlined"}
						onClick={() => conditionFieldArray.append({ propertyName: "", propertyValue: "" })}
						sx={{ alignSelf: "flex-start" }}
					/>
				</VerticalBox>
			)}
		</>
	);
}
